/**
 * Sawyer 08 meds-feeding (A1+G10) — the Meds & feeding list for one day.
 *
 * Sawyer (memo1): "if we could get medication and feeding reports on there,
 * that would be huge, so we know … all the dogs that are in-house that need
 * medication that night. So, like, AM and PM … some dogs are on lunches and
 * they're not boarding … a quick link in day list. And then you can also
 * find that under reports."
 *
 * One `/care/day` read feeds this list, the Reports tab, the printout and
 * the med pass (DESIGN-PHILOSOPHY #1). The dog's name is the hero (#3).
 * Nothing is dropped: dogs whose medication is only text from Gingr sit in
 * their own block, a medication with no placeable time says so, and a meal
 * the system assumed says "(house default)" (#5). Every control is ≥44px
 * (#7). Five states: loading, empty, error + Retry, truncated, ready.
 *
 * Deploy-window rule: every server field this reads is new with the server
 * deploy; against an older server the read 403s/404s and the page shows its
 * error state, never a half-rendered list.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AteNoneBadge } from '../care-log/AteNoneBadge.js';
import { Link } from 'react-router-dom';
import {
  fetchCareDay,
  setVisitLunch,
  type CareDayDog,
  type CareDayResponse,
  type CareDayService,
} from '../../lib/meds-feeding-api.js';
import { formatClock, formatSlots } from '../../lib/care-labels.js';
import { MedsFeedingEditor, type EditorDog } from './MedsFeedingEditor.js';
import { MedsFeedingPrintLayout } from './MedsFeedingPrintLayout.js';
import { medTimesText } from './med-line.js';
import {
  daycareDogsOffNoonList,
  doseStatusText,
  indexCareDay,
  longDateLabel,
  mealLine,
  prnText,
  serviceLabel,
  visitTag,
} from './care-day-format.js';
import './meds-feeding.css';

export type ReportState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: CareDayResponse };

export interface MedsFeedingReportProps {
  apiUrl: string;
  date: string;
  service: CareDayService;
  /** The Day list's service-type id, resolved by the server (lane 02's link). */
  serviceTypeId?: string | null;
  onServiceChange: (s: CareDayService) => void;
  /** Rendered in the toolbar (the page's date nav, or the Reports date picker). */
  dateControl: JSX.Element;
  /** h1 on its own page, h2 inside the Reports tab. */
  titleAs?: 'h1' | 'h2';
}

const SERVICES: ReadonlyArray<[CareDayService, string]> = [
  ['all', 'All'],
  ['boarding', 'Boarding'],
  ['daycare', 'Daycare'],
];

export function editorDogFrom(d: CareDayDog): EditorDog {
  return {
    id: d.dogId,
    name: d.name,
    medications: d.medications.filter((m) => m.isActive),
    medicationNotes: d.profile ? d.profile.medicationNotes : d.medicationNotes,
    feedingNotes: d.profile ? d.profile.feedingNotes : d.feeding.notesSource === 'dog' ? d.feeding.notes : null,
    foodType: d.feeding.foodType,
    feedingMethod: d.feeding.method,
    feedingSlots: d.feeding.feedingSlots,
  };
}

export function useCareDay(apiUrl: string, date: string, service: CareDayService, serviceTypeId?: string | null) {
  const [state, setState] = useState<ReportState>({ kind: 'loading' });
  const load = useCallback(
    async (opts: { quiet?: boolean } = {}) => {
      if (!opts.quiet) setState({ kind: 'loading' });
      const res = await fetchCareDay(apiUrl, date, service, serviceTypeId ?? null);
      if (res.data) setState({ kind: 'ready', data: res.data });
      else if (!opts.quiet) setState({ kind: 'error', message: "Couldn't load meds & feeding." });
    },
    [apiUrl, date, service, serviceTypeId],
  );
  useEffect(() => {
    void load();
  }, [load]);
  return { state, load, setState };
}

export function MedsFeedingReport({
  apiUrl,
  date,
  service,
  serviceTypeId,
  onServiceChange,
  dateControl,
  titleAs = 'h1',
}: MedsFeedingReportProps): JSX.Element {
  const { state, load } = useCareDay(apiUrl, date, service, serviceTypeId);
  const [printing, setPrinting] = useState(false);
  const [editing, setEditing] = useState<CareDayDog | null>(null);
  const Title = titleAs;

  const data = state.kind === 'ready' ? state.data : null;
  const effectiveService = data?.service ?? service;

  return (
    <div className="ferro-mf-report" data-testid="meds-feeding-report">
      <div className="ferro-mf-toolbar" data-testid="meds-feeding-toolbar">
        <div className="ferro-mf-toolbar__row">
          <div className="ferro-mf-toolbar__title">
            <Title className="ferro-mf-title">Meds &amp; feeding</Title>
            {data?.facilityName ? <p className="ferro-mf-toolbar__facility">{data.facilityName}</p> : null}
          </div>
          <div className="ferro-mf-actions">
            {data?.canLogDoses ? (
              <Link className="ferro-btn ferro-btn--ghost ferro-mf-btn" to={`/med-pass?date=${date}`} data-testid="meds-feeding-to-medpass">
                Med pass →
              </Link>
            ) : null}
            <button
              type="button"
              className="ferro-btn ferro-btn--outline ferro-mf-btn"
              onClick={() => setPrinting(true)}
              disabled={!data}
              data-testid="meds-feeding-print"
            >
              Print / Save as PDF
            </button>
          </div>
        </div>
        <div className="ferro-mf-toolbar__row">
          {dateControl}
          <div className="ferro-mf-chips" role="group" aria-label="Which dogs">
            {SERVICES.map(([key, label]) => (
              <button
                key={key}
                type="button"
                className="ferro-mf-chip"
                aria-pressed={effectiveService === key}
                onClick={() => onServiceChange(key)}
                data-testid={`meds-feeding-service-${key}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {data ? (
          <p className="ferro-mf-toolbar__updated" data-testid="meds-feeding-updated">
            {longDateLabel(data.date)} · Updated {formatClock(data.generatedAt, data.timeZone)}
          </p>
        ) : null}
      </div>

      {state.kind === 'loading' ? (
        <div className="ferro-mf-state" role="status" aria-live="polite" data-testid="meds-feeding-loading">
          <p className="ferro-mf-state__text">Loading meds &amp; feeding…</p>
          <span className="ferro-mf-skeleton" aria-hidden="true" />
          <span className="ferro-mf-skeleton ferro-mf-skeleton--short" aria-hidden="true" />
        </div>
      ) : null}

      {state.kind === 'error' ? (
        <div className="ferro-mf-error" role="alert" data-testid="meds-feeding-error">
          <p className="ferro-mf-error__text">{state.message}</p>
          <p className="ferro-mf-state__text">Check the connection, then try again.</p>
          <div className="ferro-mf-actions">
            <button type="button" className="ferro-btn ferro-btn--outline ferro-mf-btn" onClick={() => void load()} data-testid="meds-feeding-retry">
              Retry
            </button>
          </div>
        </div>
      ) : null}

      {data ? (
        <CareDayBody
          apiUrl={apiUrl}
          data={data}
          onEdit={setEditing}
          onChanged={() => void load({ quiet: true })}
        />
      ) : null}

      {printing && data ? <MedsFeedingPrintLayout data={data} onClose={() => setPrinting(false)} autoPrint /> : null}

      {editing ? (
        <MedsFeedingEditor
          apiUrl={apiUrl}
          dog={editorDogFrom(editing)}
          footnote="Past medications are on the dog profile."
          onClose={() => setEditing(null)}
          onChanged={() => void load({ quiet: true })}
        />
      ) : null}
    </div>
  );
}

/**
 * The Reports tab's copy of the list (US6): same component, its service
 * filter held here so Reports.tsx carries no state of its own for it.
 */
export function MedsFeedingReportTab({
  apiUrl,
  date,
  dateControl,
}: {
  apiUrl: string;
  date: string;
  dateControl: JSX.Element;
}): JSX.Element {
  const [service, setService] = useState<CareDayService>('all');
  return (
    <MedsFeedingReport
      apiUrl={apiUrl}
      date={date}
      service={service}
      onServiceChange={setService}
      dateControl={dateControl}
      titleAs="h2"
    />
  );
}

function CareDayBody({
  apiUrl,
  data,
  onEdit,
  onChanged,
}: {
  apiUrl: string;
  data: CareDayResponse;
  onEdit: (d: CareDayDog) => void;
  onChanged: () => void;
}): JSX.Element {
  const idx = useMemo(() => indexCareDay(data), [data]);
  const tz = data.timeZone;

  if (data.dogs.length === 0) {
    return (
      <p className="ferro-mf-empty ferro-mf-empty--page" data-testid="meds-feeding-empty">
        No dogs are booked in for {longDateLabel(data.date)}.
      </p>
    );
  }

  // Sawyer #1295 — the "Ate none" badge sits on a dog's FIRST head only: a
  // dog listed under AM, Noon and PM is still warned about once.
  const warned = new Set<string>();
  const ateNoneOnce = (d: CareDayDog): unknown => {
    if (warned.has(d.dogId)) return null;
    warned.add(d.dogId);
    return d.ateNone;
  };

  // The "Edit meds & feeding" button sits on a dog's FIRST line only.
  const seen = new Set<string>();
  const firstAppearance = (dogId: string) => {
    if (seen.has(dogId)) return false;
    seen.add(dogId);
    return true;
  };

  const unplaced = data.notesOnly.length + data.unscheduledMeds.length;
  const atHome = data.atHomeMeds ?? [];
  const offNoon = data.canSetLunch ? daycareDogsOffNoonList(data) : [];

  return (
    <>
      {data.truncated ? (
        <div className="ferro-mf-banner" role="status" data-testid="meds-feeding-truncated">
          This list is cut short: more dogs are booked than one page can show. Filter by Boarding or Daycare to see the rest.
        </div>
      ) : null}

      {unplaced > 0 ? (
        <section className="ferro-mf-group" aria-labelledby="mf-notes-title" data-testid="meds-feeding-notes-only">
          <h3 id="mf-notes-title" className="ferro-mf-group__title">
            Medication notes — times not set <span className="ferro-mf-group__count">{unplaced}</span>
          </h3>
          <ul className="ferro-mf-entries">
            {data.notesOnly.map((dogId) => {
              const d = idx.dogs.get(dogId);
              if (!d) return null;
              firstAppearance(dogId);
              return (
                <li key={`n-${dogId}`} className="ferro-mf-entry">
                  <DogHead dog={d} data={data} ateNone={ateNoneOnce(d)} />
                  <p className="ferro-mf-entry__notes">{d.medicationNotes}</p>
                  {data.canEditMedsFeeding ? (
                    <div className="ferro-mf-actions">
                      <button type="button" className="ferro-btn ferro-btn--ghost ferro-mf-btn" onClick={() => onEdit(d)} data-testid="meds-feeding-setup">
                        Set up meds
                      </button>
                    </div>
                  ) : null}
                </li>
              );
            })}
            {data.unscheduledMeds.map(({ dogId, medicationId }) => {
              const d = idx.dogs.get(dogId);
              const m = idx.meds.get(medicationId);
              if (!d || !m) return null;
              const first = firstAppearance(dogId);
              return (
                <li key={`u-${medicationId}`} className="ferro-mf-entry">
                  <DogHead dog={d} data={data} ateNone={ateNoneOnce(d)} />
                  <p className="ferro-mf-dose__name">
                    {[m.name, m.dosage].filter(Boolean).join(' ')} — {medTimesText(m)}
                    {m.slotSource === 'frequency_default' ? ' (run card shows AM)' : ''}
                  </p>
                  {data.canEditMedsFeeding && first ? (
                    <div className="ferro-mf-actions">
                      <button type="button" className="ferro-btn ferro-btn--ghost ferro-mf-btn" onClick={() => onEdit(d)}>
                        Set times
                      </button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      <div className="ferro-mf-groups">
        {data.groups.map((g) => (
          <section
            key={g.slot}
            className={`ferro-mf-group ferro-mf-group--${g.slot}`}
            aria-labelledby={`mf-group-${g.slot}`}
            data-testid={`meds-feeding-group-${g.slot}`}
          >
            <h3 id={`mf-group-${g.slot}`} className="ferro-mf-group__title">
              {g.label} <span className="ferro-mf-group__count">{g.entries.length}</span>
            </h3>
            {g.entries.length === 0 ? (
              <p className="ferro-mf-empty">Nothing due.</p>
            ) : (
              <ul className="ferro-mf-entries">
                {g.entries.map((e) => {
                  const d = idx.dogs.get(e.dogId);
                  if (!d) return null;
                  const first = firstAppearance(d.dogId);
                  return (
                    <li key={`${g.slot}-${e.dogId}`} className="ferro-mf-entry" data-testid={`meds-feeding-entry-${g.slot}-${e.dogId}`}>
                      <DogHead dog={d} data={data} ateNone={ateNoneOnce(d)} />
                      {d.allergies ? (
                        <p className="ferro-mf-allergy">
                          <strong>Allergies:</strong> {d.allergies}
                        </p>
                      ) : null}
                      {e.meal ? <p className="ferro-mf-meal">{mealLine(d, e.meal)}</p> : null}
                      {e.doses.map((dose) => {
                        const m = idx.meds.get(dose.medicationId);
                        if (!m) return null;
                        const statusText =
                          g.slot === 'as_needed' ? prnText(dose.status, tz) : doseStatusText(dose.status, tz);
                        return (
                          <div key={dose.medicationId} className="ferro-mf-dose">
                            <MedicationLine med={m} />
                            {statusText ? (
                              <span className="ferro-mf-status" data-outcome={dose.status.latest?.outcome ?? ''}>
                                {statusText}
                              </span>
                            ) : null}
                          </div>
                        );
                      })}
                      <div className="ferro-mf-actions">
                        {g.slot === 'midday' && d.service === 'daycare' && data.canSetLunch ? (
                          <LunchToggle apiUrl={apiUrl} dog={d} onChanged={onChanged} />
                        ) : null}
                        {first && data.canEditMedsFeeding ? (
                          <button
                            type="button"
                            className="ferro-btn ferro-btn--ghost ferro-mf-btn"
                            onClick={() => onEdit(d)}
                            data-testid={`meds-feeding-edit-${d.dogId}`}
                          >
                            Edit meds &amp; feeding
                          </button>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            {g.slot === 'midday' && offNoon.length > 0 ? (
              <div className="ferro-mf-offnoon" data-testid="meds-feeding-no-lunch">
                <h4 className="ferro-mf-group__subtitle">
                  Daycare, no lunch today <span className="ferro-mf-group__count">{offNoon.length}</span>
                </h4>
                <ul className="ferro-mf-entries">
                  {offNoon.map((d) => {
                    const first = firstAppearance(d.dogId);
                    return (
                      <li key={`nl-${d.dogId}`} className="ferro-mf-entry" data-testid={`meds-feeding-no-lunch-${d.dogId}`}>
                        <DogHead dog={d} data={data} ateNone={ateNoneOnce(d)} />
                        <div className="ferro-mf-actions">
                          <LunchToggle apiUrl={apiUrl} dog={d} onChanged={onChanged} />
                          {first && data.canEditMedsFeeding ? (
                            <button
                              type="button"
                              className="ferro-btn ferro-btn--ghost ferro-mf-btn"
                              onClick={() => onEdit(d)}
                              data-testid={`meds-feeding-edit-${d.dogId}`}
                            >
                              Edit meds &amp; feeding
                            </button>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </section>
        ))}
      </div>

      {atHome.length > 0 ? (
        <section className="ferro-mf-group ferro-mf-group--home" aria-labelledby="mf-home-title" data-testid="meds-feeding-at-home">
          <h3 id="mf-home-title" className="ferro-mf-group__title">
            Given at home <span className="ferro-mf-group__count">{atHome.length}</span>
          </h3>
          <ul className="ferro-mf-entries">
            {atHome.map(({ dogId, medicationId, slots }) => {
              const d = idx.dogs.get(dogId);
              const m = idx.meds.get(medicationId);
              if (!d || !m) return null;
              return (
                <li key={`h-${medicationId}`} className="ferro-mf-entry" data-testid={`meds-feeding-at-home-${medicationId}`}>
                  <DogHead dog={d} data={data} ateNone={ateNoneOnce(d)} />
                  <p className="ferro-mf-dose__name">
                    {[m.name, m.dosage].filter(Boolean).join(' ')} — {formatSlots(slots)}, not due during this visit
                  </p>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
    </>
  );
}

/**
 * One medication as the floor reads it. The single place a medication line
 * renders on the report and the med pass, so a later tag (lane 10's lockbox
 * flag) is one edit here.
 */
export function MedicationLine({ med }: { med: CareDayDog['medications'][number] }): JSX.Element {
  const head = [med.name, med.dosage].filter(Boolean).join(' ');
  return (
    <span className="ferro-mf-dose__name">
      {head}
      {med.instructions ? <span className="ferro-mf-dose__instr"> — {med.instructions}</span> : null}
      {med.slotSource === 'frequency' ? <span className="ferro-mf-dose__note"> (from frequency — set times to confirm)</span> : null}
      {!med.isActive ? <span className="ferro-mf-dose__note"> (stopped since)</span> : null}
    </span>
  );
}

export function DogHead({
  dog,
  data,
  ateNone,
}: {
  dog: CareDayDog;
  data: CareDayResponse;
  /** Sawyer #1295 — the raw server warning, passed on the dog's first head only. */
  ateNone?: unknown;
}): JSX.Element {
  return (
    <div className="ferro-mf-entry__head">
      {data.canOpenProfiles ? (
        <Link className="ferro-mf-dogname" to={`/dogs/${dog.dogId}`}>
          {dog.name}
        </Link>
      ) : (
        <span className="ferro-mf-dogname">{dog.name}</span>
      )}
      <span className="ferro-mf-entry__meta">
        {[dog.breed, serviceLabel(dog.service), visitTag(dog, data.timeZone), dog.ownerName].filter(Boolean).join(' · ')}
      </span>
      <AteNoneBadge ateNone={ateNone} dogName={dog.name} testId={`meds-feeding-ate-none-${dog.dogId}`} />
    </div>
  );
}

function LunchToggle({ apiUrl, dog, onChanged }: { apiUrl: string; dog: CareDayDog; onChanged: () => void }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const current = dog.feeding.lunchOverride;
  const choose = async (v: boolean | null) => {
    setBusy(true);
    setError(null);
    const res = await setVisitLunch(apiUrl, dog.bookingId, dog.dogId, v);
    setBusy(false);
    if (res.data) onChanged();
    else setError(res.error ?? "Couldn't save. Try again.");
  };
  return (
    <div className="ferro-mf-lunch" role="group" aria-label={`Lunch today for ${dog.name}`} data-testid={`meds-feeding-lunch-${dog.dogId}`}>
      {(
        [
          [true, 'Lunch today'],
          [false, 'No lunch'],
          [null, 'Usual'],
        ] as const
      ).map(([v, label]) => (
        <button key={label} type="button" className="ferro-mf-chip" aria-pressed={current === v} disabled={busy} onClick={() => void choose(v)}>
          {label}
        </button>
      ))}
      {error ? <span className="ferro-mf-field__error">{error}</span> : null}
    </div>
  );
}
